/**
 * Upload, map, reconcile. SPEC-0001.
 *
 * The mapping layer is the feature BP.md §8 calls the real technical
 * challenge: every customer's export has different column names and quirks,
 * and "a mapping layer that lets a non-technical user align their own file's
 * columns to the expected schema (rather than requiring an exact template) is
 * what separates a usable product from a one-off script."
 *
 * The screen is **one column per file**, each carrying its own file picker,
 * its own saved-mapping choice, and its own column table. An earlier version
 * grouped by step instead — both files, then both mappings — which left the
 * link between a file and its mapping purely spatial and easy to lose. Putting
 * them in one column makes "this mapping is for this file" structural.
 *
 * Options below the columns are deliberately NOT per file: the duplicate
 * policy and role aliases apply to the join across both sides, so repeating
 * them per column would imply a choice that does not exist.
 *
 * Two rules shape this screen:
 *  - Suggestions are pre-populated and always user-confirmable. Picklii never
 *    applies a guess. (SPEC-0001 step 3; the same stance as ADR-0010.)
 *  - The header preview here is a UX affordance. The server re-parses the
 *    whole file and its parse is authoritative. (SPEC-0001 step 4.)
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	applyProfile,
	fieldsFor,
	suggestMapping,
	SOLD_CANONICAL_FIELDS,
	SOLD_REQUIRED_CANONICAL_FIELDS,
	type ColumnMapping,
	type ExtractedSoldRow,
	type MappingProfile,
	type SoldCanonicalField,
	type SourceType,
} from '@picklii/shared';
import type { RoleAlias } from '@picklii/recon';
import { AppShell } from '../components/AppShell';
import { Icon } from '../components/Icon';
import { api } from '../lib/api';
import { readHeaderRow } from '../lib/headers';
import { rowsToCsv } from '../lib/csv';

/**
 * SPEC-0006 (Draft) — NOT approved for production customer use; see that
 * spec's open questions 1 and 5. This screen is built ahead of that sign-off
 * so the feature is ready the moment both close.
 */
const FIELD_LABELS: Record<SoldCanonicalField, string> = {
	project_id: 'Project ID',
	client_name: 'Client',
	role: 'Role',
	sold_hours: 'Sold hours',
	rate: 'Rate',
	sow_signed_date: 'SOW signed date',
	go_live_date: 'Go-live date',
	end_date: 'End date',
};

/** One uploaded document's extraction, still editable, not yet part of a run. */
interface ExtractedDoc {
	fileName: string;
	rows: ExtractedSoldRow[];
}

interface Side {
	/**
	 * More than one file only ever applies to the sold side: a change order
	 * often arrives as its own export rather than a second line in the
	 * original one (issue #30). The budget side just never has more than one
	 * entry here — a PSA is amended in place, not appended to.
	 */
	files: File[];
	/** Headers of `files[0]`, which drives the mapping for every file on this side. */
	headers: string[];
	mapping: ColumnMapping;
	/** Which saved profile is applied. Empty means guess from the headers. */
	profileId: string;
	/** Name to save this mapping under. Empty means do not save. */
	saveAs: string;
}

const EMPTY: Side = { files: [], headers: [], mapping: {}, profileId: '', saveAs: '' };

type DuplicatePolicy = 'sum' | 'latest' | 'reject';

const SIDES: ReadonlyArray<{ source: SourceType; title: string; hint: string; multiple: boolean }> = [
	{
		source: 'sold',
		title: 'Sold hours',
		hint:
			'From your CPQ, signed SOW data, or a finance export. Select more than one file if a ' +
			'change order arrived as its own export rather than a line in this one.',
		multiple: true,
	},
	{
		source: 'budget',
		title: 'PSA budget',
		hint: 'From Certinia, OpenAir, Kantata, or similar.',
		multiple: false,
	},
];

/** One or more files sharing a mapping, and the profile it came from or will become. */
function FileColumn({
	source,
	title,
	hint,
	multiple,
	side,
	profiles,
	saving,
	onFile,
	onChange,
	onSave,
}: {
	source: SourceType;
	title: string;
	hint: string;
	multiple: boolean;
	side: Side;
	profiles: readonly MappingProfile[];
	saving: boolean;
	onFile(files: File[]): void;
	onChange(next: Side): void;
	onSave(): void;
}) {
	const missing = fieldsFor(source).filter((f) => f.required && side.mapping[f.key] === undefined);
	const hasFiles = side.files.length > 0;

	return (
		<section className="card bg-base-200 border-base-300 col-span-12 border shadow-xs xl:col-span-6">
			<div className="card-body gap-4">
				<div>
					<h2 className="card-title text-base">{title}</h2>
					<p className="text-base-content/70 text-sm">{hint}</p>
				</div>

				<div className="flex flex-col gap-1.5">
					<input
						type="file"
						accept=".csv,text/csv"
						multiple={multiple}
						className="file-input file-input-bordered w-full"
						aria-label={`${title} CSV`}
						onChange={(e) => {
							const files = Array.from(e.target.files ?? []);
							if (files.length > 0) onFile(files);
						}}
					/>
					<span className="text-base-content/70 text-xs">
						{!hasFiles
							? 'Choose a CSV to map its columns.'
							: side.files.length === 1
								? `${side.headers.length} columns found in ${side.files[0]!.name}`
								: `${side.headers.length} columns found in ${side.files[0]!.name} ` +
									`(+${side.files.length - 1} more file${side.files.length > 2 ? 's' : ''}, ` +
									'same mapping applied to each)'}
					</span>
				</div>

				{hasFiles && (
					<>
						<label className="flex flex-col gap-1.5">
							<span className="text-sm font-medium">Mapping</span>
							{/*
							 * Rendered as soon as a file is chosen, even with nothing saved
							 * yet: a control that only appears once you already have profiles
							 * cannot teach you that profiles exist.
							 */}
							<select
								className="select select-bordered select-sm mt-1"
								value={side.profileId}
								aria-label={`Saved mapping for ${title}`}
								onChange={(e) => onChange({ ...side, profileId: e.target.value })}>
								<option value="">Guess from my headers</option>
								{profiles.map((p) => (
									<option key={p.id} value={p.id}>
										Use {p.name}
									</option>
								))}
							</select>
							<span className="text-base-content/70 text-xs">
								{profiles.length === 0
									? `No saved ${title.toLowerCase()} mappings yet. Name one below to reuse it next time.`
									: 'Only columns your file actually has get filled in.'}
							</span>
						</label>

						<div className="flex flex-col gap-1.5">
							<span className="text-sm font-medium">Save this mapping for reuse</span>
							<div className="join">
								<input
									className="input input-bordered input-sm join-item w-full"
									placeholder="Name it to save it"
									value={side.saveAs}
									aria-label={`Profile name for ${title}`}
									onChange={(e) => onChange({ ...side, saveAs: e.target.value })}
									onKeyDown={(e) => {
										if (e.key === 'Enter' && side.saveAs.trim() !== '') {
											e.preventDefault();
											onSave();
										}
									}}
								/>
								<button
									className="btn btn-sm btn-primary join-item"
									disabled={saving || side.saveAs.trim() === ''}
									onClick={onSave}>
									{saving ? <span className="loading loading-spinner loading-xs" /> : 'Save'}
								</button>
							</div>
							<span className="text-base-content/70 text-xs">
								Optional. Saving selects it above; an existing name updates that mapping
								rather than making a second one.
							</span>
						</div>

						<div>
							<div className="mb-2 flex items-center justify-between">
								<span className="text-sm font-medium">Columns</span>
								{missing.length > 0 && (
									<span className="badge badge-warning badge-sm">
										{missing.length} still to map
									</span>
								)}
							</div>
							<div className="overflow-x-auto">
								<table className="table table-sm">
									<thead>
										<tr>
											<th>Picklii needs</th>
											<th>Your column</th>
										</tr>
									</thead>
									<tbody>
										{fieldsFor(source).map((field) => (
											<tr key={field.key}>
												<td>
													<span className="font-medium">{field.label}</span>
													{field.required && (
														<span className="badge badge-ghost badge-xs ms-2">required</span>
													)}
													<span className="text-base-content/70 block font-mono text-xs">
														{field.key}
													</span>
												</td>
												<td>
													<select
														className="select select-bordered select-sm w-full"
														value={side.mapping[field.key] ?? ''}
														aria-label={`${title}: column for ${field.label}`}
														onChange={(e) => {
															const mapping = { ...side.mapping };
															if (e.target.value === '') delete mapping[field.key];
															else mapping[field.key] = e.target.value;
															onChange({ ...side, mapping });
														}}>
														<option value="">— not mapped —</option>
														{side.headers
															// A header already mapped to a different field is hidden here —
															// two canonical fields sharing one column would silently read
															// the same value into both, and (for extraction) would emit a
															// synthetic CSV with a duplicate header that PapaParse then
															// collapses, dropping one field's value entirely.
															.filter(
																(header) =>
																	header === side.mapping[field.key] ||
																	!Object.values(side.mapping).includes(header),
															)
															.map((header) => (
																<option key={header} value={header}>
																	{header}
																</option>
															))}
													</select>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>
					</>
				)}
			</div>
		</section>
	);
}

/**
 * SPEC-0006 (Draft) — extraction's confirm screen. Deliberately a plain
 * editable table of the six canonical fields rather than a column-mapping
 * table like `FileColumn`'s: there is no header row to map, since Gemini
 * already targets the canonical schema directly. Every field renders
 * pre-filled but editable, and nothing here is submitted until the run
 * itself is — the same *pre-fill, never auto-apply* rule SPEC-0001 already
 * applies to saved mapping profiles.
 */
function ExtractionCard({
	docs,
	extracting,
	extractError,
	onFiles,
	onFieldChange,
	onRemoveRow,
	onRemoveDoc,
}: {
	docs: readonly ExtractedDoc[];
	extracting: boolean;
	extractError: string | null;
	onFiles(files: File[]): void;
	onFieldChange(docIndex: number, rowIndex: number, field: SoldCanonicalField, value: string): void;
	onRemoveRow(docIndex: number, rowIndex: number): void;
	onRemoveDoc(docIndex: number): void;
}) {
	return (
		<section className="card bg-base-200 border-base-300 col-span-12 border shadow-xs">
			<div className="card-body gap-4">
				<div>
					<h2 className="card-title text-base">Or extract from a signed document</h2>
					<p className="text-base-content/70 text-sm">
						Upload a signed SOW, Quote, or change-order PDF and Picklii proposes the sold-hours
						fields for you to review. A field it could not find is left blank, never guessed —
						you confirm or correct every value before it counts toward a run.
					</p>
				</div>

				<div className="flex flex-col gap-1.5">
					<input
						type="file"
						accept=".pdf,application/pdf"
						multiple
						disabled={extracting}
						className="file-input file-input-bordered w-full"
						aria-label="Signed SOW or Quote PDF"
						onChange={(e) => {
							const files = Array.from(e.target.files ?? []);
							if (files.length > 0) onFiles(files);
							e.target.value = '';
						}}
					/>
					{extracting && (
						<span className="text-base-content/70 flex items-center gap-2 text-xs">
							<span className="loading loading-spinner loading-xs" />
							Reading the document…
						</span>
					)}
					{extractError !== null && <span className="text-error text-xs">{extractError}</span>}
				</div>

				{docs.map((doc, docIndex) => (
					<div key={docIndex} className="border-base-300 rounded-box border p-3">
						<div className="mb-2 flex items-center justify-between">
							<span className="font-mono text-xs">{doc.fileName}</span>
							<button className="btn btn-xs btn-outline" onClick={() => onRemoveDoc(docIndex)}>
								Remove document
							</button>
						</div>
						{doc.rows.length === 0 ? (
							<p className="text-base-content/70 text-xs">No line items found in this document.</p>
						) : (
							<div className="overflow-x-auto">
								<table className="table table-sm">
									<thead>
										<tr>
											{SOLD_CANONICAL_FIELDS.map((field) => (
												<th key={field}>{FIELD_LABELS[field]}</th>
											))}
											<th />
										</tr>
									</thead>
									<tbody>
										{doc.rows.map((row, rowIndex) => (
											<tr key={rowIndex}>
												{SOLD_CANONICAL_FIELDS.map((field) => (
													<td key={field}>
														<input
															className="input input-bordered input-sm w-full"
															value={row[field] ?? ''}
															placeholder={row[field] === null ? 'not found — enter it' : ''}
															aria-label={`${FIELD_LABELS[field]}, row ${rowIndex + 1} of ${doc.fileName}`}
															onChange={(e) =>
																onFieldChange(docIndex, rowIndex, field, e.target.value)
															}
														/>
													</td>
												))}
												<td>
													<button
														className="btn btn-xs btn-outline"
														onClick={() => onRemoveRow(docIndex, rowIndex)}>
														Remove
													</button>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
					</div>
				))}
			</div>
		</section>
	);
}

export function NewRun() {
	const navigate = useNavigate();
	const [sides, setSides] = useState<Record<SourceType, Side>>({ sold: EMPTY, budget: EMPTY });
	const [aliases, setAliases] = useState<RoleAlias[]>([]);
	const [duplicatePolicy, setDuplicatePolicy] = useState<DuplicatePolicy>('reject');
	const [profiles, setProfiles] = useState<MappingProfile[]>([]);
	const [busy, setBusy] = useState(false);
	const [savingSide, setSavingSide] = useState<SourceType | null>(null);
	const [error, setError] = useState<string | null>(null);

	// SPEC-0006 (Draft) — NOT approved for production customer use. Sold-side
	// only, per that spec's scope: there is no signed document on the budget
	// side to extract from.
	const [extractedDocs, setExtractedDocs] = useState<ExtractedDoc[]>([]);
	const [extracting, setExtracting] = useState(false);
	const [extractError, setExtractError] = useState<string | null>(null);

	useEffect(() => {
		api.listProfiles().then(setProfiles, () => setProfiles([]));
	}, []);

	/** Derive a side's columns from its headers and its chosen profile. */
	function remap(source: SourceType, side: Side): Side {
		if (side.files.length === 0) return side;
		const profile = profiles.find((p) => p.id === side.profileId);
		return {
			...side,
			mapping:
				profile === undefined
					? suggestMapping(side.headers, source)
					: applyProfile(profile, side.headers, source),
		};
	}

	function update(source: SourceType, next: Side) {
		// Choosing a different profile re-derives the columns. Editing one column
		// must not, or the edit would be undone as soon as it was made.
		const changedProfile = next.profileId !== sides[source].profileId;
		const settled = changedProfile ? remap(source, next) : next;
		setSides((current) => ({ ...current, [source]: settled }));

		if (!changedProfile) return;
		const profile = profiles.find((p) => p.id === settled.profileId);
		if (profile === undefined || profile.aliases.length === 0) return;
		// Aliases ride along with the profile, merged rather than replacing what
		// is already there — two profiles may each contribute some.
		setAliases((current) => {
			const seen = new Set(current.map((a) => `${a.from}=>${a.to}`));
			return [...current, ...profile.aliases.filter((a) => !seen.has(`${a.from}=>${a.to}`))];
		});
	}

	async function pick(source: SourceType, files: File[]) {
		// Headers come from the first file only: every file on a side shares one
		// mapping (a change order export is still sold-hours shaped), so there is
		// exactly one set of columns to confirm regardless of how many files.
		const headers = await readHeaderRow(files[0]!);
		// The save name is deliberately NOT pre-filled. Saving is opt-in: an empty
		// box means this mapping is for this run only, which is the common case.
		const base: Side = { ...sides[source], files, headers };
		setSides((current) => ({ ...current, [source]: remap(source, base) }));
	}

	/**
	 * Save this side's columns as a reusable profile, then select it.
	 *
	 * Saving is its own action rather than a side effect of reconciling, so the
	 * user sees the result immediately: the mapping dropdown switches from
	 * "Guess from my headers" to the profile they just made, which is the
	 * confirmation that it exists.
	 */
	async function saveMapping(source: SourceType) {
		const side = sides[source];
		const name = side.saveAs.trim();
		if (name === '') return;
		setSavingSide(source);
		setError(null);
		try {
			const saved = await api.saveProfile({
				name,
				sourceType: source,
				columns: side.mapping,
				aliases,
			});
			setProfiles((current) => {
				const without = current.filter((p) => p.id !== saved.id);
				return [...without, saved].sort((a, b) => a.name.localeCompare(b.name));
			});
			setSides((current) => ({
				...current,
				[source]: { ...current[source], profileId: saved.id ?? '', saveAs: '' },
			}));
		} catch (cause) {
			setError(cause instanceof Error ? cause.message : 'Could not save the mapping.');
		} finally {
			setSavingSide(null);
		}
	}

	// SPEC-0006 (Draft) — NOT approved for production customer use.
	const hasSoldExtraction = extractedDocs.some((d) => d.rows.length > 0);

	/**
	 * One Gemini call per document (SPEC-0006's recommendation); a failure on
	 * one does not block the others still in flight — `allSettled`, not `all`,
	 * so a bad scan among three uploads doesn't discard the two good
	 * extractions (and their already-billed Gemini calls) along with it.
	 */
	async function extractDocuments(files: File[]) {
		setExtracting(true);
		setExtractError(null);
		try {
			const outcomes = await Promise.allSettled(files.map((file) => api.extractSoldDocument(file)));
			const succeeded = outcomes.flatMap((o) => (o.status === 'fulfilled' ? [o.value] : []));
			const failed = outcomes.flatMap((o, i) => (o.status === 'rejected' ? [files[i]!.name] : []));
			if (succeeded.length > 0) {
				setExtractedDocs((current) => [...current, ...succeeded]);
			}
			if (failed.length > 0) {
				setExtractError(`Could not read: ${failed.join(', ')}.`);
			}
		} finally {
			setExtracting(false);
		}
	}

	function updateExtractedField(
		docIndex: number,
		rowIndex: number,
		field: SoldCanonicalField,
		value: string,
	) {
		setExtractedDocs((current) =>
			current.map((doc, di) =>
				di !== docIndex
					? doc
					: {
							...doc,
							rows: doc.rows.map((row, ri) =>
								ri !== rowIndex ? row : { ...row, [field]: value === '' ? null : value },
							),
						},
			),
		);
	}

	function removeExtractedRow(docIndex: number, rowIndex: number) {
		setExtractedDocs((current) =>
			current.map((doc, di) =>
				di !== docIndex ? doc : { ...doc, rows: doc.rows.filter((_, ri) => ri !== rowIndex) },
			),
		);
	}

	function removeExtractedDoc(docIndex: number) {
		setExtractedDocs((current) => current.filter((_, di) => di !== docIndex));
	}

	// One SOW plus up to ten change orders, and one budget file — the real
	// ceiling `server.ts` enforces on the multipart request itself. Past this,
	// busboy aborts the whole upload with a bare "Premature close" mid-stream;
	// catching it here means an actionable message instead of that.
	const MAX_TOTAL_FILES = 11;
	const extractedFileCount = extractedDocs.filter((d) => d.rows.length > 0).length;
	const totalFileCount = sides.sold.files.length + extractedFileCount + (sides.budget.files.length > 0 ? 1 : 0);

	// A blank required field in an extracted row is never sent as-is: a blank
	// `sold_hours` would otherwise coerce to 0 and reconcile silently (the
	// exact "guessed" value the feature promises never to produce). Each
	// count names one still-missing field across every row still in the list.
	const missingExtractedFields = new Map<SoldCanonicalField, number>();
	for (const doc of extractedDocs) {
		for (const row of doc.rows) {
			for (const field of SOLD_REQUIRED_CANONICAL_FIELDS) {
				if (row[field] === null || row[field]!.trim() === '') {
					missingExtractedFields.set(field, (missingExtractedFields.get(field) ?? 0) + 1);
				}
			}
		}
	}

	const blockers = [
		...SIDES.flatMap(({ source, title }) => {
			const side = sides[source];
			// An extraction-only sold side has no header row to map — the canonical
			// fields stand in for themselves at submit time.
			if (source === 'sold' && side.files.length === 0 && hasSoldExtraction) return [];
			if (side.files.length === 0) return [`Choose a ${title.toLowerCase()} file`];
			return fieldsFor(source)
				.filter((f) => f.required && side.mapping[f.key] === undefined)
				.map((f) => `${title}: map ${f.label}`);
		}),
		...[...missingExtractedFields.entries()].map(
			([field, count]) => `Fill in ${FIELD_LABELS[field]} for ${count} extracted row${count === 1 ? '' : 's'}`,
		),
		...(totalFileCount > MAX_TOTAL_FILES
			? [`Too many files for one run (${totalFileCount} of ${MAX_TOTAL_FILES} max) — remove some`]
			: []),
	];

	async function submit() {
		const { sold, budget } = sides;
		if ((sold.files.length === 0 && !hasSoldExtraction) || budget.files.length === 0) return;
		const soldProfileName = profiles.find((p) => p.id === sold.profileId)?.name ?? null;
		const budgetProfileName = profiles.find((p) => p.id === budget.profileId)?.name ?? null;
		setBusy(true);
		setError(null);
		try {
			// SPEC-0006 (Draft): a confirmed extraction becomes a CSV that reuses
			// whichever header name the sold mapping already assigns each
			// canonical field, so it parses under the exact same shared mapping
			// every sold file on this run uses (SPEC-0001 §1). The identity
			// fallback (a field mapping to its own canonical name) is added only
			// for fields at least one extraction actually populated — an unmapped
			// optional field neither source provides gets left out entirely,
			// rather than claiming a column no uploaded CSV actually has.
			const fieldsExtractionProvides = SOLD_CANONICAL_FIELDS.filter((f) =>
				extractedDocs.some((doc) => doc.rows.some((row) => row[f] !== null && row[f]!.trim() !== '')),
			);
			const effectiveSoldMapping: ColumnMapping = hasSoldExtraction
				? {
						...sold.mapping,
						...Object.fromEntries(
							fieldsExtractionProvides
								.filter((f) => sold.mapping[f] === undefined)
								.map((f) => [f, f]),
						),
					}
				: sold.mapping;

			const extractedFiles = extractedDocs
				.filter((doc) => doc.rows.length > 0)
				.map((doc) => {
					const headers = SOLD_CANONICAL_FIELDS.map((f) => effectiveSoldMapping[f] ?? f);
					const rows = doc.rows.map((row) => SOLD_CANONICAL_FIELDS.map((f) => row[f] ?? ''));
					return new File([rowsToCsv(headers, rows)], `${doc.fileName}.csv`, { type: 'text/csv' });
				});

			const run = await api.createRun(
				{ sold: [...sold.files, ...extractedFiles], budget: budget.files[0]! },
				{
					soldMapping: effectiveSoldMapping,
					budgetMapping: budget.mapping,
					aliases,
					duplicatePolicy,
					// Which saved mapping each side used, for the record. Saving is a
					// separate, explicit action now, so nothing is created here.
					usedProfiles: {
						...(soldProfileName === null ? {} : { sold: soldProfileName }),
						...(budgetProfileName === null ? {} : { budget: budgetProfileName }),
					},
				},
			);
			navigate(`/runs/${run.id}`);
		} catch (cause) {
			// Row-level validation failures no longer block submission — the run
			// completes and shows its exclusions on the run page itself. What can
			// still fail here is request-shaped (malformed mapping, duplicate rows
			// rejected, an encoding Picklii won't guess at), so one message is
			// enough. SPEC-0001 §Open questions #2.
			setError(cause instanceof Error ? cause.message : 'The reconciliation failed.');
		} finally {
			setBusy(false);
		}
	}

	const anyFile = sides.sold.files.length > 0 || sides.budget.files.length > 0 || hasSoldExtraction;

	return (
		<AppShell title="New reconciliation">
			{SIDES.map(({ source, title, hint, multiple }) => (
				<FileColumn
					key={source}
					source={source}
					title={title}
					hint={hint}
					multiple={multiple}
					side={sides[source]}
					profiles={profiles.filter((p) => p.sourceType === source)}
					saving={savingSide === source}
					onFile={(files) => void pick(source, files)}
					onChange={(next) => update(source, next)}
					onSave={() => void saveMapping(source)}
				/>
			))}

			<ExtractionCard
				docs={extractedDocs}
				extracting={extracting}
				extractError={extractError}
				onFiles={(files) => void extractDocuments(files)}
				onFieldChange={updateExtractedField}
				onRemoveRow={removeExtractedRow}
				onRemoveDoc={removeExtractedDoc}
			/>

			<p className="text-base-content/70 col-span-12 text-xs">
				Files are parsed in memory and never stored. Only the parsed rows and the findings are
				kept.
			</p>

			{anyFile && (
				<section className="card bg-base-200 border-base-300 col-span-12 border shadow-xs">
					<div className="card-body gap-4">
						<div>
							<h2 className="card-title text-base">Options for this run</h2>
							<p className="text-base-content/70 text-sm">
								These apply to the comparison across both files, not to either one alone.
								Role names are matched after normalizing whitespace and casing; anything
								beyond that needs an alias you confirm — Picklii never guesses a pairing.
							</p>
						</div>

						<div className="flex max-w-md flex-col gap-1.5">
							<span className="text-sm font-medium">Repeated project + role rows</span>
							<select
								className="select select-bordered select-sm"
								value={duplicatePolicy}
								onChange={(e) => setDuplicatePolicy(e.target.value as DuplicatePolicy)}>
								<option value="reject">Stop and show me the duplicates</option>
								<option value="sum">Add their hours together</option>
								<option value="latest">Keep the most recent row</option>
							</select>
							{/* RESOLVED — SPEC-0001 #1 (2026-10-16): no fixed default, by
							    design. Asked per run precisely so that no layer decides it
							    silently. */}
							<span className="text-base-content/70 text-xs">
								Picklii never assumes which policy a repeat should follow — this is asked for
								every run.
							</span>
						</div>

						<div className="flex flex-col gap-1.5">
							<span className="text-sm font-medium">Role aliases</span>
							{aliases.map((alias, index) => (
								<div key={index} className="join mb-2 w-full max-w-2xl">
									<input
										className="input input-bordered input-sm join-item w-full"
										placeholder="Sr. Consultant"
										value={alias.from}
										onChange={(e) =>
											setAliases(
												aliases.map((a, i) => (i === index ? { ...a, from: e.target.value } : a)),
											)
										}
									/>
									<span className="join-item btn btn-sm btn-disabled">means</span>
									<input
										className="input input-bordered input-sm join-item w-full"
										placeholder="Senior Consultant"
										value={alias.to}
										onChange={(e) =>
											setAliases(
												aliases.map((a, i) => (i === index ? { ...a, to: e.target.value } : a)),
											)
										}
									/>
									<button
										className="btn btn-sm btn-outline join-item"
										onClick={() => setAliases(aliases.filter((_, i) => i !== index))}>
										Remove
									</button>
								</div>
							))}
							<button
								className="btn btn-sm btn-outline self-start"
								onClick={() => setAliases([...aliases, { from: '', to: '' }])}>
								Add an alias
							</button>
						</div>
					</div>
				</section>
			)}

			<section className="col-span-12 flex flex-col gap-4">
				{error !== null && (
					<div className="alert alert-error alert-soft" role="alert">
						<span>{error}</span>
					</div>
				)}

				{/*
				 * The action sits at the end of the flow, on the right. What is still
				 * missing stays on the left via me-auto, so the button lands in the
				 * same place whether or not anything is blocking it — a control that
				 * moves depending on state is harder to aim at.
				 */}
				<div className="flex flex-wrap items-center justify-end gap-3">
					{blockers.length > 0 && (
						<span className="text-base-content/70 me-auto text-sm">{blockers.join(' · ')}</span>
					)}
					<button
						className="btn btn-primary"
						// `extracting` matters here, not just `busy`: without it, an
						// in-flight document dropped right before clicking Reconcile is
						// silently excluded from the run with no signal that it was.
						disabled={busy || extracting || blockers.length > 0}
						onClick={() => void submit()}>
						{busy ? (
							<span className="loading loading-spinner loading-sm" />
						) : (
							<Icon name="reconcile" />
						)}
						Reconcile
					</button>
				</div>
			</section>
		</AppShell>
	);
}
